/**
 * Llama chest screen (self-contained DOM overlay, like the villager trade and
 * horse screens): the llama's 3 x strength chest slots. Click a filled slot to
 * take the stack, click an empty (or same-item) slot with a stack in hand to
 * put it in. Opened by TraderSystem.openLlama.
 */

import type { Llama } from '../entities/trader/llama';
import type { Stack } from '../game/types';

export interface LlamaScreenDeps {
  /** The stack in the selected hotbar slot. */
  held(): Stack;
  /** Remove `n` from the held stack. */
  takeHeld(n: number): void;
  /** Hand a stack to the player (leftovers are dropped by the glue). */
  give(s: Stack): void;
  nameOf(id: number): string;
  iconFor(id: number): HTMLCanvasElement | null;
  /** Largest stack for an item id. */
  maxStack(id: number): number;
}

const K = 3;

export class LlamaScreen {
  private readonly root: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private llama: Llama | null = null;
  private onClose: (() => void) | null = null;

  constructor(parent: HTMLElement, private readonly d: LlamaScreenDeps) {
    this.root = document.createElement('div');
    this.root.style.cssText = [
      'position:fixed', 'inset:0', 'display:none', 'align-items:center', 'justify-content:center',
      'z-index:60', 'font:13px/1.4 monospace', 'color:#3f3f3f',
      'background:linear-gradient(rgba(16,16,16,0.75),rgba(16,16,16,0.82))', 'user-select:none',
    ].join(';');
    const panel = document.createElement('div');
    panel.style.cssText = [
      'background:#c6c6c6', 'border:2px solid #000', 'box-shadow:inset 2px 2px 0 #fff, inset -2px -2px 0 #555',
      `padding:${4 * K}px ${6 * K}px ${6 * K}px`, 'image-rendering:pixelated',
    ].join(';');
    this.body = document.createElement('div');
    panel.appendChild(this.body);
    this.root.appendChild(panel);
    this.root.addEventListener('mousedown', (e) => { if (e.target === this.root) this.close(); });
    window.addEventListener('keydown', (e) => { if (this.open && (e.code === 'Escape' || e.code === 'KeyE')) { e.preventDefault(); this.close(); } });
    parent.appendChild(this.root);
  }

  get open(): boolean { return this.root.style.display !== 'none'; }

  show(l: Llama, onClose: () => void): void {
    this.llama = l; this.onClose = onClose;
    try { document.exitPointerLock?.(); } catch { /* not locked */ }
    this.root.style.display = 'flex';
    this.render();
  }

  close(): void {
    if (!this.open) return;
    this.root.style.display = 'none';
    this.llama = null;
    const cb = this.onClose; this.onClose = null;
    cb?.();
  }

  private click(i: number): void {
    const l = this.llama;
    if (!l) return;
    const slot = l.items[i], held = this.d.held();
    if (slot.count > 0 && slot.id > 0) {
      this.d.give({ ...slot });
      l.items[i] = { id: 0, count: 0, dmg: 0 };
    } else if (held.count > 0 && held.id > 0) {
      l.items[i] = { ...held, count: Math.min(held.count, this.d.maxStack(held.id)) };
      this.d.takeHeld(l.items[i].count);
    }
    this.render();
  }

  render(): void {
    const l = this.llama;
    if (!l) return;
    this.body.textContent = '';
    const title = document.createElement('div');
    title.textContent = `${l.isTrader ? 'Trader Llama' : 'Llama'}  -  Strength ${l.strength}  -  Health ${Math.ceil(l.health)}/${Math.ceil(l.maxHealth)}`;
    title.style.cssText = `margin-bottom:${3 * K}px;font-weight:bold;`;
    this.body.appendChild(title);
    const grid = document.createElement('div');
    grid.style.cssText = `display:grid;grid-template-columns:repeat(${l.strength},${18 * K}px);gap:0;`;
    l.items.forEach((s, i) => {
      const box = document.createElement('div');
      box.style.cssText = `position:relative;width:${18 * K}px;height:${18 * K}px;box-sizing:border-box;background:#8b8b8b;`
        + 'box-shadow:inset 2px 2px 0 #373737, inset -2px -2px 0 #fff;cursor:pointer;display:flex;align-items:center;justify-content:center;';
      if (s.count > 0 && s.id > 0) {
        const icon = this.d.iconFor(s.id);
        if (icon) {
          const c = document.createElement('canvas');
          c.width = icon.width; c.height = icon.height;
          c.getContext('2d')?.drawImage(icon, 0, 0);
          c.style.cssText = `width:${16 * K}px;height:${16 * K}px;image-rendering:pixelated;`;
          box.appendChild(c);
        } else box.textContent = this.d.nameOf(s.id).slice(0, 2);
        box.title = this.d.nameOf(s.id);
        if (s.count > 1) {
          const n = document.createElement('span');
          n.textContent = String(s.count);
          n.style.cssText = 'position:absolute;right:2px;bottom:-1px;font-size:12px;font-weight:bold;color:#fff;text-shadow:1px 1px 0 #000;';
          box.appendChild(n);
        }
      }
      box.onclick = () => this.click(i);
      grid.appendChild(box);
    });
    this.body.appendChild(grid);
    const hint = document.createElement('div');
    hint.textContent = 'Click a slot to take / store  ·  Esc / E to close';
    hint.style.cssText = `margin-top:${3 * K}px;font-size:11px;color:#5a5a5a;`;
    this.body.appendChild(hint);
  }
}
